import React, { useState, useEffect, useContext, useRef } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { UserContext } from "../contexts/UserContext";
import OrdenTareaModal from "./OrdenTareaModal";

// ─── Constantes ───────────────────────────────────────────────────────────────
const ESTADOS = [
  "Orden de Compra Emitida",
  "Proceso de Diseño y Desarrollo",
  "Producción",
];

const ESTADO_COLORS = {
  "Orden de Compra Emitida":        { bg: "#EEF2FF", border: "#818CF8", text: "#3730A3", dot: "#6366F1" },
  "Proceso de Diseño y Desarrollo": { bg: "#FFF7ED", border: "#FB923C", text: "#9A3412", dot: "#F97316" },
  "Producción":                     { bg: "#F0FDF4", border: "#4ADE80", text: "#166534", dot: "#22C55E" },
};

const BASE = process.env.REACT_APP_API_BASE || "http://localhost/twinpack/php";

const formatFecha = (str) => {
  if (!str) return "—";
  // Fechas date-only (YYYY-MM-DD) → parsear como local para evitar el corrimiento
  // de un día por zona horaria (new Date("YYYY-MM-DD") se interpreta como UTC).
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(str).trim());
  const d = m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(str);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit", year: "numeric" });
};

const diasDesde = (str) => {
  if (!str) return null;
  const diff = Math.floor((new Date() - new Date(str)) / 86400000);
  return diff;
};

// ─── Estilos ──────────────────────────────────────────────────────────────────
const S = {
  page: {
    minHeight: "100vh", background: "#f3f0f4",
    padding: "28px 20px", fontFamily: "'Segoe UI', system-ui, sans-serif",
  },
  header: {
    maxWidth: 1200, margin: "0 auto 20px",
    display: "flex", alignItems: "flex-end",
    justifyContent: "space-between", flexWrap: "wrap", gap: 12,
  },
  title: { fontSize: 22, fontWeight: 700, color: "#121212", margin: 0 },
  subtitle: { fontSize: 13, color: "#888", marginTop: 2 },
  filters: {
    display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center",
  },
  select: {
    padding: "8px 12px", border: "1.5px solid #e0d6dc",
    borderRadius: 8, fontSize: 13, color: "#333",
    background: "#fff", outline: "none", cursor: "pointer", minWidth: 150,
  },
  btnClear: {
    padding: "8px 14px", background: "transparent",
    border: "1.5px solid #c990b0", borderRadius: 8,
    fontSize: 13, color: "#9f0059", cursor: "pointer", fontWeight: 600,
  },
  board: {
    maxWidth: 1200, margin: "0 auto",
    // minmax(0, 1fr) permite que las columnas se compriman aunque el contenido
    // tenga strings largos sin espacios (ej. CUIT). Sin minmax(0,...), una card
    // con texto largo fuerza a su columna a expandirse y comprime a las otras.
    display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 16,
  },
  column: (isDragOver) => ({
    background: isDragOver ? "#f0e6ec" : "#ede8ed",
    borderRadius: 14, padding: "12px",
    minHeight: 400, transition: "background 0.15s",
    border: isDragOver ? "2px dashed #9f0059" : "2px solid transparent",
    minWidth: 0, // permite que la columna se comprima sin importar el contenido
    overflow: "hidden",
  }),
  colHeader: (colors) => ({
    display: "flex", alignItems: "center", justifyContent: "space-between",
    marginBottom: 12, padding: "8px 12px",
    background: colors.bg, borderRadius: 9,
    border: `1.5px solid ${colors.border}`,
  }),
  colTitle: (colors) => ({
    fontSize: 12, fontWeight: 700, letterSpacing: "0.06em",
    textTransform: "uppercase", color: colors.text,
    display: "flex", alignItems: "center", gap: 6,
  }),
  colDot: (colors) => ({
    width: 8, height: 8, borderRadius: "50%", background: colors.dot,
  }),
  colCount: (colors) => ({
    fontSize: 11, fontWeight: 700, color: colors.text,
    background: "#fff", padding: "2px 8px", borderRadius: 20,
    border: `1px solid ${colors.border}`,
  }),
  card: (isDragging) => ({
    background: "#fff", borderRadius: 11,
    padding: "14px 14px 10px",
    marginBottom: 10,
    boxShadow: isDragging
      ? "0 8px 24px rgba(159,0,89,0.18)"
      : "0 1px 6px rgba(0,0,0,0.07)",
    cursor: "grab", transition: "box-shadow 0.15s, transform 0.1s",
    transform: isDragging ? "rotate(1.5deg)" : "none",
    opacity: isDragging ? 0.85 : 1,
    border: "1.5px solid #f0e6ec",
    // Que strings largos sin espacios (CUITs, etc.) corten en vez de expandir el ancho
    overflowWrap: "anywhere",
    wordBreak: "break-word",
  }),
  cardCliente: {
    fontSize: 14, fontWeight: 700, color: "#121212", marginBottom: 2,
  },
  cardProveedor: {
    fontSize: 12, color: "#9f0059", fontWeight: 600, marginBottom: 8,
  },
  cardMeta: {
    display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8,
  },
  metaChip: {
    fontSize: 11, color: "#666", background: "#f7f4f6",
    padding: "2px 8px", borderRadius: 20,
  },
  diasChip: (dias) => ({
    fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 20,
    background: dias <= 3 ? "#d4f5e2" : dias <= 10 ? "#fff3cd" : "#fde8e8",
    color: dias <= 3 ? "#0a6e3f" : dias <= 10 ? "#856404" : "#a00",
  }),
  cardObs: {
    fontSize: 11, color: "#888", marginTop: 4,
    whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
  },
  emptyCol: {
    textAlign: "center", padding: "32px 0",
    color: "#bbb", fontSize: 13,
  },
  // ── Modal detalle ──
  overlay: {
    position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)",
    display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
  },
  modal: {
    background: "#fff", borderRadius: 16, padding: "28px 28px 20px",
    width: "100%", maxWidth: 500,
    boxShadow: "0 8px 40px rgba(0,0,0,0.15)",
  },
  modalTitle: { fontSize: 18, fontWeight: 700, color: "#9f0059", marginBottom: 4 },
  modalSub: { fontSize: 13, color: "#666", marginBottom: 20 },
  modalGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 20 },
  modalField: { display: "flex", flexDirection: "column", gap: 2 },
  modalLabel: { fontSize: 11, fontWeight: 700, color: "#aaa", textTransform: "uppercase", letterSpacing: "0.06em" },
  modalValue: { fontSize: 14, color: "#222", fontWeight: 500 },
  historialTitle: { fontSize: 12, fontWeight: 700, color: "#9f0059", marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.06em" },
  historialItem: {
    display: "flex", alignItems: "flex-start", gap: 10,
    padding: "8px 0", borderBottom: "1px solid #f5f0f3",
  },
  historialDot: { width: 8, height: 8, borderRadius: "50%", background: "#9f0059", marginTop: 5, flexShrink: 0 },
  historialText: { fontSize: 13, color: "#333" },
  historialMeta: { fontSize: 11, color: "#aaa", marginTop: 2 },
  modalFooter: { display: "flex", justifyContent: "flex-end", marginTop: 20 },
  btnClose: {
    padding: "9px 20px", background: "#9f0059",
    border: "none", borderRadius: 8,
    fontSize: 14, fontWeight: 700, cursor: "pointer", color: "#fff",
  },
};

// ─── Card de orden ────────────────────────────────────────────────────────────
const OrdenCard = ({ orden, onDragStart, onClick, onEntregar }) => {
  const diasEnEstado = diasDesde(orden.fecha_estado ?? orden.created_at);
  const [dragging, setDragging] = useState(false);

  return (
    <div
      style={S.card(dragging)}
      draggable
      onDragStart={(e) => { setDragging(true); onDragStart(e, orden); }}
      onDragEnd={() => setDragging(false)}
    >
      <div onClick={() => onClick(orden)}>
        <div style={S.cardCliente}>{orden.cliente}</div>
        <div style={S.cardProveedor}>{orden.proveedor}</div>
        <div style={S.cardMeta}>
          <span style={S.metaChip}>{orden.usuario}</span>
          {orden.fecha_entrega && (
            <span style={S.metaChip}>🗓 {formatFecha(orden.fecha_entrega)}</span>
          )}
          {diasEnEstado !== null && (
            <span style={{ ...S.diasChip(diasEnEstado), fontSize: 11 }} title="Días en este estado">
              {diasEnEstado}d en estado
            </span>
          )}
          {orden.monto_total && (
            <span style={S.metaChip}>
              {orden.moneda === "USD" ? "USD " : "$"}
              {parseFloat(orden.monto_total).toLocaleString("es-AR")}
              {orden.moneda === "USD" && (
                <span style={{ marginLeft: 4, fontSize: 10, fontWeight: 700, padding: "0px 5px",
                  borderRadius: 20, background: "#fffbeb", color: "#92400e", border: "1px solid #fcd34d" }}>
                  USD
                </span>
              )}
            </span>
          )}
          {/* Chip de progreso: nuevo modelo (bundles) o legacy (sub_entregas) */}
          {orden.total_bundles > 0 ? (() => {
            const total    = orden.total_bundles;
            const entregados = orden.bundles_entregados;
            const todas    = entregados === total;
            return (
              <span style={{
                fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 20,
                background: todas ? "#dcfce7" : "#dbeafe",
                color:      todas ? "#16a34a" : "#1d4ed8",
              }}>
                {todas ? "✓ Todas las entregas" : `📦 ${entregados}/${total} entregas`}
              </span>
            );
          })() : orden.total_subentregas > 0 ? (() => {
            const total    = orden.total_subentregas;
            const completas = orden.subentregas_completas;
            const todas    = completas === total;
            return (
              <span style={{
                fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 20,
                background: todas ? "#dcfce7" : "#dbeafe",
                color:      todas ? "#16a34a" : "#1d4ed8",
              }}>
                {todas ? "✓ Todas entregadas" : `📦 ${completas}/${total} entregas`}
              </span>
            );
          })() : null}
        </div>
        {orden.observaciones && (
          <div style={S.cardObs} title={orden.observaciones}>
            {orden.observaciones}
          </div>
        )}
      </div>
      <button
        style={{
          marginTop: 10, width: "100%",
          padding: "6px 0", borderRadius: 7,
          border: "1.5px solid #4ADE80",
          background: "#F0FDF4", color: "#166534",
          fontSize: 12, fontWeight: 700, cursor: "pointer",
        }}
        onClick={(e) => { e.stopPropagation(); onEntregar(orden); }}
      >
        ✓ Marcar como entregada
      </button>
    </div>
  );
};

// ─── Modal de detalle ─────────────────────────────────────────────────────────
const BUNDLE_COLORS_KANBAN = ["#9f0059", "#7c3aed", "#0369a1", "#065f46", "#92400e", "#1e3a5f"];

const OrdenModal = ({ orden, historial, detalle, onClose, onMarcarSubEntrega, onMarcarBundle, onSaved }) => {
  const [editFecha,   setEditFecha]   = React.useState(detalle?.fecha ?? orden.fecha_entrega ?? "");
  const [editObs,     setEditObs]     = React.useState(orden.observaciones ?? "");
  const [saving,      setSaving]      = React.useState(false);
  const [notas,       setNotas]       = React.useState([]);
  const [notaInput,   setNotaInput]   = React.useState("");
  const [savingNota,  setSavingNota]  = React.useState(false);
  const [showTarea,   setShowTarea]   = React.useState(false);

  // Extraer userId del localStorage (mismo patrón que Inicio.jsx)
  const userId = React.useMemo(() => {
    try { const u = localStorage.getItem("user"); return u ? JSON.parse(u).id : null; } catch { return null; }
  }, []);

  // Sincronizar cuando llegan datos del detalle
  React.useEffect(() => {
    if (detalle?.fecha) setEditFecha(detalle.fecha.slice(0, 10));
  }, [detalle?.fecha]);

  // Cargar notas al montar
  const traerNotas = React.useCallback(() => {
    axios.post(`${BASE}/orden_notas_listar.php`, new URLSearchParams({ orden_id: orden.id }))
      .then(r => setNotas(Array.isArray(r.data) ? r.data : []))
      .catch(() => setNotas([]));
  }, [orden.id]);

  React.useEffect(() => { traerNotas(); }, [traerNotas]);

  const handleGuardarNota = async () => {
    if (!notaInput.trim()) return;
    setSavingNota(true);
    try {
      const fd = new URLSearchParams({
        orden_id:   orden.id,
        usuario_id: userId || 0,
        contenido:  notaInput.trim(),
      });
      const r = await axios.post(`${BASE}/orden_notas_guardar.php`, fd);
      if (r.data?.success) {
        setNotaInput("");
        // Agregar la nota devuelta al inicio de la lista (sin refetch)
        if (r.data.nota) setNotas(prev => [r.data.nota, ...prev]);
        else traerNotas();
      } else {
        toast.error(r.data?.mensaje || "Error al guardar nota");
      }
    } catch {
      toast.error("Error de conexión");
    } finally {
      setSavingNota(false);
    }
  };

  if (!orden) return null;
  const archivos     = detalle?.archivos ?? [];
  const itemsDetalle = detalle?.items    ?? [];
  const bundlesDetalle = detalle?.bundles ?? [];

  const handleGuardar = async () => {
    setSaving(true);
    try {
      const fd = new URLSearchParams();
      fd.append("orden_id",      orden.id);
      fd.append("observaciones", editObs);
      fd.append("fecha_entrega", editFecha);
      const res = await axios.post(`${BASE}/actualizar_oc.php`, fd);
      if (res.data.success) {
        toast.success("Orden actualizada");
        if (onSaved) onSaved();
      } else {
        toast.error(res.data.message || "Error al guardar");
      }
    } catch {
      toast.error("Error de conexión");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={S.overlay} onClick={onClose}>
      <div style={{ ...S.modal, maxWidth: 600, maxHeight: "90vh", overflowY: "auto" }} onClick={(e) => e.stopPropagation()}>
        <p style={S.modalTitle}>{orden.cliente}</p>
        <p style={S.modalSub}>{orden.proveedor} · Orden #{orden.id}</p>
        <div style={S.modalGrid}>
          <div style={S.modalField}>
            <span style={S.modalLabel}>Usuario</span>
            <span style={S.modalValue}>{orden.usuario}</span>
          </div>
          <div style={S.modalField}>
            <span style={S.modalLabel}>Monto</span>
            <span style={S.modalValue}>
              {orden.monto_total ? (
                <>
                  {orden.moneda === "USD" ? "USD " : "$"}
                  {parseFloat(orden.monto_total).toLocaleString("es-AR")}
                  {orden.moneda === "USD" && orden.tipo_cambio && (
                    <span style={{ display: "block", fontSize: 11, color: "#888", marginTop: 1 }}>
                      ≈ ${(parseFloat(orden.monto_total) * parseFloat(orden.tipo_cambio)).toLocaleString("es-AR", { minimumFractionDigits: 0 })} ARS
                    </span>
                  )}
                </>
              ) : "—"}
            </span>
          </div>
          <div style={S.modalField}>
            <span style={S.modalLabel}>Fecha cotización</span>
            <span style={S.modalValue}>{formatFecha(orden.created_at)}</span>
          </div>
          <div style={S.modalField}>
            <span style={S.modalLabel}>Estado actual</span>
            <span style={S.modalValue}>{orden.estado_actual}</span>
          </div>
          {orden.fecha_estado && (
            <div style={S.modalField}>
              <span style={S.modalLabel}>En este estado desde</span>
              <span style={S.modalValue}>{formatFecha(orden.fecha_estado)}</span>
            </div>
          )}

          {/* ── Campos editables ── */}
          <div style={S.modalField}>
            <span style={S.modalLabel}>Fecha de entrega</span>
            <input
              type="date"
              value={editFecha}
              onChange={(e) => setEditFecha(e.target.value)}
              style={{ padding: "6px 10px", border: "1.5px solid #e0d6dc", borderRadius: 7, fontSize: 13, color: "#222", background: "#fafafa" }}
            />
          </div>
          <div style={{ ...S.modalField, gridColumn: "1 / -1" }}>
            <span style={S.modalLabel}>Observaciones</span>
            <textarea
              value={editObs}
              onChange={(e) => setEditObs(e.target.value)}
              rows={3}
              style={{ padding: "7px 10px", border: "1.5px solid #e0d6dc", borderRadius: 7, fontSize: 13, color: "#222", background: "#fafafa", resize: "vertical", fontFamily: "inherit", width: "100%", boxSizing: "border-box" }}
            />
          </div>
        </div>

        {/* ── Bundles de entrega (nuevo modelo) ── */}
        {bundlesDetalle.length > 0 && (
          <>
            <p style={S.historialTitle}>Entregas ({bundlesDetalle.length})</p>
            {bundlesDetalle.map((b, bIdx) => {
              const color = BUNDLE_COLORS_KANBAN[bIdx % BUNDLE_COLORS_KANBAN.length];
              return (
                <div key={b.id} style={{
                  marginBottom: 12, borderRadius: 9,
                  border: `1.5px solid ${b.entregado ? "#bbf7d0" : "#e0d6dc"}`,
                  overflow: "hidden",
                }}>
                  {/* Header del bundle */}
                  <div style={{
                    background: b.entregado ? "#f0fdf4" : color,
                    padding: "9px 14px",
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                  }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: b.entregado ? "#166534" : "#fff" }}>
                      📦 Bundle {b.numero} — {formatFecha(b.fecha_entrega)}
                    </span>
                    {b.entregado ? (
                      <span style={{ fontSize: 11, color: "#166534", fontWeight: 600 }}>
                        ✅ Entregado {b.fecha_entregado ? formatFecha(b.fecha_entregado) : ""}
                      </span>
                    ) : (
                      <button
                        style={{
                          fontSize: 11, padding: "3px 12px", borderRadius: 5,
                          background: "#dcfce7", color: "#166534",
                          border: "none", cursor: "pointer", fontWeight: 700,
                        }}
                        onClick={() => onMarcarBundle(b.id, 1)}
                      >
                        ✓ Marcar como entregado
                      </button>
                    )}
                  </div>

                  {/* Ítems del bundle */}
                  <div style={{ padding: "8px 14px", background: b.entregado ? "#f8fdf9" : "#fafafa" }}>
                    {(b.items ?? []).length === 0 ? (
                      <span style={{ fontSize: 12, color: "#bbb" }}>Sin productos</span>
                    ) : (
                      (b.items ?? []).map((it) => (
                        <div key={it.id} style={{
                          display: "flex", justifyContent: "space-between", alignItems: "baseline",
                          padding: "3px 0", borderBottom: "1px solid #f0e8ec",
                        }}>
                          <span style={{ fontSize: 13, color: "#333" }}>
                            {it.descripcion} <strong style={{ color: "#555" }}>× {it.cantidad}</strong>
                          </span>
                          {it.categoria && (
                            <span style={{ fontSize: 11, color: "#9f0059", fontWeight: 600 }}>{it.categoria}</span>
                          )}
                        </div>
                      ))
                    )}
                    {b.entregado && (
                      <div style={{ marginTop: 8, textAlign: "right" }}>
                        <button
                          style={{
                            fontSize: 11, padding: "2px 10px", borderRadius: 4,
                            background: "#fee2e2", color: "#dc2626",
                            border: "none", cursor: "pointer", fontWeight: 700,
                          }}
                          onClick={() => onMarcarBundle(b.id, 0)}
                        >
                          ↩ Desmarcar
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </>
        )}

        {/* ── Ítems planos (legacy: sin bundle, o entrega única) ── */}
        {itemsDetalle.length > 0 && (
          <>
            <p style={S.historialTitle}>
              {bundlesDetalle.length > 0 ? "Ítems adicionales" : `Ítems (${itemsDetalle.length})`}
            </p>
            {itemsDetalle.map((item) => (
              <div key={item.id} style={{ marginBottom: 10, padding: "10px 12px", background: "#f7f4f6", borderRadius: 8, border: "1px solid #ede8ed" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 6 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: "#222" }}>
                    {item.descripcion} × {item.cantidad}
                  </span>
                  {item.categoria && (
                    <span style={{ fontSize: 11, color: "#9f0059", fontWeight: 600 }}>{item.categoria}</span>
                  )}
                </div>

                {item.sub_entregas?.length > 0 ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    {item.sub_entregas.map((se, si) => (
                      <div key={se.id} style={{
                        display: "flex", alignItems: "center", justifyContent: "space-between",
                        padding: "5px 10px", borderRadius: 6,
                        background: se.entregada ? "#f0fdf4" : "#fff",
                        border: `1px solid ${se.entregada ? "#bbf7d0" : "#e5e7eb"}`,
                      }}>
                        <span style={{ fontSize: 12, color: se.entregada ? "#166534" : "#374151" }}>
                          {se.entregada ? "✅" : "⬜"} Tramo {si + 1}: <strong>{se.cantidad}</strong> unid → {formatFecha(se.fecha_entrega)}
                          {se.entregada && se.fecha_entrega_real && (
                            <span style={{ color: "#6b7280", marginLeft: 6, fontSize: 11 }}>
                              (entregado {formatFecha(se.fecha_entrega_real)})
                            </span>
                          )}
                        </span>
                        <button
                          style={{
                            fontSize: 11, padding: "2px 10px", borderRadius: 4,
                            cursor: "pointer", fontWeight: 700, border: "none",
                            background: se.entregada ? "#fee2e2" : "#dcfce7",
                            color:      se.entregada ? "#dc2626" : "#16a34a",
                            whiteSpace: "nowrap",
                          }}
                          onClick={() => onMarcarSubEntrega(se.id, se.entregada ? 0 : 1)}
                        >
                          {se.entregada ? "↩ Desmarcar" : "✓ Marcar"}
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <span style={{ fontSize: 11, color: "#bbb" }}>Entrega única</span>
                )}
              </div>
            ))}
          </>
        )}

        {/* Archivos adjuntos */}
        <p style={S.historialTitle}>Archivos adjuntos</p>
        {archivos.length === 0 ? (
          <p style={{ fontSize: 13, color: "#aaa", marginBottom: 16 }}>Sin archivos adjuntos.</p>
        ) : (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 16 }}>
            {archivos.map((f, i) => (
              <a
                key={i}
                href={`${BASE}/uploads_php/${f.nombre}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: "flex", alignItems: "center", gap: 6,
                  padding: "5px 12px", background: "#f0e6ec",
                  borderRadius: 20, fontSize: 12, color: "#9f0059",
                  fontWeight: 600, textDecoration: "none",
                  border: "1px solid #d9b8cc",
                }}
              >
                📎 {f.nombre}
              </a>
            ))}
          </div>
        )}

        <p style={S.historialTitle}>Historial de cambios</p>
        {historial.length === 0 ? (
          <p style={{ fontSize: 13, color: "#aaa" }}>Sin movimientos registrados aún.</p>
        ) : (
          historial.map((h, i) => (
            <div key={i} style={S.historialItem}>
              <div style={S.historialDot} />
              <div>
                <div style={S.historialText}>
                  <strong>{h.estado}</strong>
                  {h.nota && ` — ${h.nota}`}
                </div>
                <div style={S.historialMeta}>
                  {h.usuario} · {formatFecha(h.fecha)}
                </div>
              </div>
            </div>
          ))
        )}

        {/* ── Notas de seguimiento ── */}
        <div style={{ borderTop: "1.5px solid #f0e6ec", paddingTop: 16, marginTop: 16 }}>
          <p style={S.historialTitle}>Notas de seguimiento</p>
          <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
            <textarea
              value={notaInput}
              onChange={(e) => setNotaInput(e.target.value)}
              placeholder="Ej: Salió de Buenos Aires, ETA 2 días…"
              rows={2}
              onKeyDown={(e) => { if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) handleGuardarNota(); }}
              style={{ flex: 1, padding: "7px 10px", border: "1.5px solid #e0d6dc", borderRadius: 7, fontSize: 13, color: "#222", background: "#fafafa", resize: "vertical", fontFamily: "inherit", boxSizing: "border-box" }}
            />
            <button
              onClick={handleGuardarNota}
              disabled={savingNota || !notaInput.trim()}
              style={{
                padding: "7px 14px",
                background: (savingNota || !notaInput.trim()) ? "#f0e6ec" : "#9f0059",
                color:      (savingNota || !notaInput.trim()) ? "#bbb"    : "#fff",
                border: "none", borderRadius: 7, fontSize: 12, fontWeight: 700,
                cursor: (savingNota || !notaInput.trim()) ? "default" : "pointer",
                alignSelf: "flex-start", whiteSpace: "nowrap",
              }}
            >
              {savingNota ? "Guardando…" : "+ Agregar"}
            </button>
          </div>
          {notas.length === 0 ? (
            <p style={{ fontSize: 12, color: "#bbb", margin: 0 }}>Sin notas aún. Agregá la primera.</p>
          ) : (
            notas.map((n, i) => (
              <div key={n.id || i} style={{
                display: "flex", gap: 10, padding: "9px 0",
                borderBottom: i < notas.length - 1 ? "1px solid #f0e6ec" : "none",
              }}>
                <div style={{
                  width: 28, height: 28, borderRadius: "50%",
                  background: "#f0e6ec", display: "flex", alignItems: "center",
                  justifyContent: "center", fontSize: 12, fontWeight: 700,
                  color: "#9f0059", flexShrink: 0, marginTop: 1,
                }}>
                  {(n.usuario_nombre || "?")[0].toUpperCase()}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 11, color: "#888", marginBottom: 3 }}>
                    <strong style={{ color: "#444" }}>{n.usuario_nombre || "—"}</strong>
                    {" · "}
                    {n.created_at
                      ? new Date(n.created_at).toLocaleString("es-AR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })
                      : "—"}
                  </div>
                  <div style={{ fontSize: 13, color: "#222", lineHeight: 1.45, whiteSpace: "pre-wrap" }}>
                    {n.contenido}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        <div style={S.modalFooter}>
          <button style={S.btnClose} onClick={onClose}>Cerrar</button>
          <button
            onClick={() => setShowTarea(true)}
            style={{
              padding: "8px 16px", background: "#fff", color: "#9f0059",
              border: "1.5px solid #9f0059", borderRadius: 8, fontSize: 13, fontWeight: 700,
              cursor: "pointer", marginLeft: 10,
            }}
          >
            📅 Crear tarea
          </button>
          <button
            onClick={handleGuardar}
            disabled={saving}
            style={{
              padding: "8px 20px", background: saving ? "#d1d5db" : "#9f0059", color: "#fff",
              border: "none", borderRadius: 8, fontSize: 13, fontWeight: 700,
              cursor: saving ? "default" : "pointer", marginLeft: 10,
            }}
          >
            {saving ? "Guardando…" : "Guardar cambios"}
          </button>
        </div>

        {showTarea && (
          <OrdenTareaModal orden={orden} userId={userId} onClose={() => setShowTarea(false)} />
        )}
      </div>
    </div>
  );
};

// ─── Modal confirmación entrega (con carga opcional de comisiones) ────────────
const ModalConfirmarEntrega = ({ orden, onConfirm, onCancel }) => {
  const montoOc = parseFloat(orden.monto_total) || 0;
  const [usuarios, setUsuarios] = useState([]);
  const [comisiones, setComisiones] = useState([]); // [{ _k, usuario_id, porcentaje }]
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    axios.post(`${BASE}/buscar_usuarios.php`)
      .then((r) => setUsuarios((Array.isArray(r.data) ? r.data : []).filter((u) => Number(u.comercial) === 1 && Number(u.activo) === 1)))
      .catch(() => {});
  }, []);

  const addRow    = () => setComisiones((p) => [...p, { _k: Math.random().toString(36).slice(2), usuario_id: "", porcentaje: "" }]);
  const removeRow = (k) => setComisiones((p) => p.filter((c) => c._k !== k));
  const updateRow = (k, f, v) => setComisiones((p) => p.map((c) => (c._k === k ? { ...c, [f]: v } : c)));

  const fmt = (n) => "$" + (parseFloat(n) || 0).toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const confirmar = async () => {
    // Validar filas con datos parciales
    for (const c of comisiones) {
      const pct = parseFloat(c.porcentaje) || 0;
      if ((c.usuario_id && pct <= 0) || (!c.usuario_id && pct > 0)) {
        toast.error("Completá usuario y porcentaje en cada comisión, o quitá la fila.");
        return;
      }
    }
    const validas = comisiones.filter((c) => c.usuario_id && (parseFloat(c.porcentaje) || 0) > 0);
    setGuardando(true);
    await onConfirm(validas.map((c) => ({ usuario_id: c.usuario_id, porcentaje: parseFloat(c.porcentaje) })));
  };

  const inp = { padding: "6px 8px", border: "1.5px solid #e0d6dc", borderRadius: 6, fontSize: 13, outline: "none", background: "#fff" };

  return (
    <div style={S.overlay} onClick={onCancel}>
      <div style={{ ...S.modal, maxWidth: 480, maxHeight: "90vh", overflowY: "auto" }} onClick={e => e.stopPropagation()}>
        <p style={S.modalTitle}>Confirmar entrega</p>
        <p style={S.modalSub}>¿Estás seguro de marcar esta orden como entregada?</p>
        <div style={{ background: "#f7f4f6", borderRadius: 10, padding: "12px 16px", marginBottom: 18 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: "#121212" }}>{orden.cliente}</div>
          <div style={{ fontSize: 13, color: "#9f0059", fontWeight: 600, marginTop: 2 }}>{orden.proveedor}</div>
          {orden.monto_total && (
            <div style={{ fontSize: 13, color: "#666", marginTop: 4 }}>{fmt(montoOc)}</div>
          )}
        </div>

        {/* Comisiones opcionales */}
        <div style={{ border: "1.5px solid #f0e6ec", borderRadius: 10, padding: "12px 14px", marginBottom: 18 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: comisiones.length ? 10 : 0 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: "#444" }}>💰 Comisiones (opcional)</span>
            <button onClick={addRow} style={{ background: "none", border: "1.5px dashed #c990b0", borderRadius: 7, color: "#9f0059", padding: "5px 12px", cursor: "pointer", fontWeight: 600, fontSize: 12 }}>
              + Agregar comisionado
            </button>
          </div>
          {comisiones.map((c) => {
            const pct = parseFloat(c.porcentaje) || 0;
            return (
              <div key={c._k} style={{ marginBottom: 10 }}>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <select style={{ ...inp, flex: 1 }} value={c.usuario_id} onChange={(e) => updateRow(c._k, "usuario_id", e.target.value)}>
                    <option value="">— Usuario —</option>
                    {usuarios.map((u) => <option key={u.id} value={u.id}>{u.nombre}</option>)}
                  </select>
                  <input style={{ ...inp, width: 70, textAlign: "right" }} type="number" min="0" step="0.01" placeholder="%" value={c.porcentaje} onChange={(e) => updateRow(c._k, "porcentaje", e.target.value)} />
                  <button onClick={() => removeRow(c._k)} style={{ background: "none", border: "none", color: "#c0396a", cursor: "pointer", fontSize: 16 }} title="Quitar">✕</button>
                </div>
                {pct > 0 && montoOc > 0 && (
                  <div style={{ fontSize: 11, color: "#9f0059", marginTop: 3, marginLeft: 2 }}>= {fmt(montoOc * pct / 100)}</div>
                )}
              </div>
            );
          })}
        </div>

        <p style={{ fontSize: 12, color: "#aaa", marginBottom: 18 }}>
          Esta acción moverá la orden al historial de entregadas y no podrá deshacerse.
        </p>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <button style={{ padding: "9px 20px", background: "transparent", border: "1.5px solid #ccc", borderRadius: 8, fontSize: 14, cursor: "pointer", color: "#555" }} onClick={onCancel} disabled={guardando}>
            Cancelar
          </button>
          <button style={{ padding: "9px 20px", background: "#166534", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: guardando ? "wait" : "pointer", color: "#fff", opacity: guardando ? 0.7 : 1 }} onClick={confirmar} disabled={guardando}>
            {guardando ? "Procesando…" : "✓ Sí, marcar como entregada"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Kanban principal ─────────────────────────────────────────────────────────
const SeguimientoKanban = () => {
  const [ordenes, setOrdenes] = useState([]);
  const [dragOver, setDragOver] = useState(null);
  const [ordenAentregar, setOrdenAentregar] = useState(null);
  const [filtroCliente, setFiltroCliente] = useState("");
  const [filtroProveedor, setFiltroProveedor] = useState("");
  const [filtroUsuario, setFiltroUsuario] = useState("");
  const [modalOrden, setModalOrden] = useState(null);
  const [historial, setHistorial] = useState([]);
  const [modalDetalle, setModalDetalle] = useState(null);
  const dragItem = useRef(null);
  const { user } = useContext(UserContext);

  const traerOrdenes = () => {
    axios.post(`${BASE}/buscar_ordenes_kanban.php`)
      .then(r => { if (Array.isArray(r.data)) setOrdenes(r.data); })
      .catch(() => toast.error("Error al cargar órdenes"));
  };

  const traerHistorial = (ordenId) => {
    axios.post(`${BASE}/buscar_historial.php`, new URLSearchParams({ orden_id: ordenId }))
      .then(r => { if (Array.isArray(r.data)) setHistorial(r.data); })
      .catch(() => setHistorial([]));
  };

  useEffect(() => { traerOrdenes(); }, []);

  // ── Drag & Drop ──────────────────────────────────────────────────────────
  const handleDragStart = (e, orden) => {
    dragItem.current = orden;
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e, estado) => {
    e.preventDefault();
    setDragOver(estado);
  };

    const handleDrop = async (e, nuevoEstado, ordenOverride) => {
  e.preventDefault();
  setDragOver(null);
  const orden = ordenOverride ?? dragItem.current;
  if (!orden || orden.estado_actual === nuevoEstado) return;

  if (nuevoEstado === "Entregado") {
    setOrdenes(prev => prev.filter(o => o.id !== orden.id));
  } else {
    setOrdenes(prev =>
      prev.map(o => o.id === orden.id ? { ...o, estado_actual: nuevoEstado } : o)
    );
  }

  try {
    const formData = new FormData();
    formData.append("orden_id", orden.id);
    formData.append("estado", nuevoEstado);
    formData.append("usuario_id", user?.id ?? 0);
    const res = await axios.post(`${BASE}/cambiar_estado_kanban.php`, formData);
    if (!res.data.success) {
      toast.error("Error al actualizar estado");
      traerOrdenes();
    } else {
      if (nuevoEstado === "Entregado") toast.success(`Orden de ${orden.cliente} marcada como entregada ✓`);
      else toast.success(`Orden movida a "${nuevoEstado}"`);
    }
  } catch {
    toast.error("Error de conexión");
    traerOrdenes();
  }
};

  const handleDragLeave = () => setDragOver(null);

  // ── Modal ────────────────────────────────────────────────────────────────
  const handleCardClick = (orden) => {
    setModalOrden(orden);
    setModalDetalle(null);
    traerHistorial(orden.id);
    axios.post(`${BASE}/getOrderDetails.php`, new URLSearchParams({ orderId: orden.id }))
      .then(r => setModalDetalle(r.data))
      .catch(() => {});
  };

  const refetchDetalle = (ordenId) =>
    axios.post(`${BASE}/getOrderDetails.php`, new URLSearchParams({ orderId: ordenId }))
      .then(r => setModalDetalle(r.data))
      .catch(() => {});

  const marcarSubEntrega = async (subId, entregada) => {
    try {
      const fd = new FormData();
      fd.append("id",        subId);
      fd.append("entregada", entregada);
      const res = await axios.post(`${BASE}/marcar_sub_entrega.php`, fd);
      if (res.data.ok) {
        if (modalOrden) refetchDetalle(modalOrden.id);
        traerOrdenes();
      } else {
        toast.error("Error al actualizar el tramo de entrega");
      }
    } catch {
      toast.error("Error de conexión");
    }
  };

  const marcarBundle = async (bundleId, entregado) => {
    try {
      const fd = new FormData();
      fd.append("bundle_id", bundleId);
      fd.append("entregado", entregado);
      const res = await axios.post(`${BASE}/marcar_entrega_bundle.php`, fd);
      if (res.data.success) {
        if (modalOrden) refetchDetalle(modalOrden.id);
        traerOrdenes();
      } else {
        toast.error("Error al actualizar el bundle");
      }
    } catch {
      toast.error("Error de conexión");
    }
  };

  // ── Filtros ──────────────────────────────────────────────────────────────
  const clientes = [...new Set(ordenes.map(o => o.cliente).filter(Boolean))].sort();
  const proveedores = [...new Set(ordenes.map(o => o.proveedor).filter(Boolean))].sort();
  const usuarios = [...new Set(ordenes.map(o => o.usuario).filter(Boolean))].sort();

  const ordenesFiltradas = ordenes.filter(o => {
    if (filtroCliente && o.cliente !== filtroCliente) return false;
    if (filtroProveedor && o.proveedor !== filtroProveedor) return false;
    if (filtroUsuario && o.usuario !== filtroUsuario) return false;
    return true;
  });

  const ordenesEnEstado = (estado) =>
    ordenesFiltradas.filter(o => o.estado_actual === estado);

  const hayFiltros = filtroCliente || filtroProveedor || filtroUsuario;

  return (
    <section style={S.page}>
      {/* Header */}
      <div style={S.header}>
        <div>
          <h2 style={S.title}>Seguimiento de órdenes</h2>
          <p style={S.subtitle}>{ordenesFiltradas.length} orden{ordenesFiltradas.length !== 1 ? "es" : ""} en curso</p>
        </div>
        <div style={S.filters}>
          <select style={S.select} value={filtroCliente} onChange={e => setFiltroCliente(e.target.value)}>
            <option value="">Todos los clientes</option>
            {clientes.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select style={S.select} value={filtroProveedor} onChange={e => setFiltroProveedor(e.target.value)}>
            <option value="">Todos los proveedores</option>
            {proveedores.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
          <select style={S.select} value={filtroUsuario} onChange={e => setFiltroUsuario(e.target.value)}>
            <option value="">Todos los usuarios</option>
            {usuarios.map(u => <option key={u} value={u}>{u}</option>)}
          </select>
          {hayFiltros && (
            <button style={S.btnClear} onClick={() => {
              setFiltroCliente(""); setFiltroProveedor(""); setFiltroUsuario("");
            }}>
              Limpiar filtros
            </button>
          )}
        </div>
      </div>

      {/* Board */}
      <div style={S.board}>
        {ESTADOS.map(estado => {
          const cols = ESTADO_COLORS[estado];
          const items = ordenesEnEstado(estado);
          const isOver = dragOver === estado;

          return (
            <div
              key={estado}
              style={S.column(isOver)}
              onDragOver={e => handleDragOver(e, estado)}
              onDragLeave={handleDragLeave}
              onDrop={e => handleDrop(e, estado)}
            >
              <div style={S.colHeader(cols)}>
                <div style={S.colTitle(cols)}>
                  <div style={S.colDot(cols)} />
                  {estado}
                </div>
                <span style={S.colCount(cols)}>{items.length}</span>
              </div>

              {items.length === 0 ? (
                <div style={S.emptyCol}>Sin órdenes</div>
              ) : (
                items.map(orden => (
                  <OrdenCard
                    key={orden.id}
                    orden={orden}
                    onDragStart={handleDragStart}
                    onClick={handleCardClick}
                    onEntregar={(orden) => setOrdenAentregar(orden)}
                  />
                ))
              )}
            </div>
          );
        })}
      </div>

      {/* Modal detalle */}
      {modalOrden && (
        <OrdenModal
          orden={modalOrden}
          historial={historial}
          detalle={modalDetalle}
          onClose={() => { setModalOrden(null); setHistorial([]); setModalDetalle(null); }}
          onMarcarSubEntrega={marcarSubEntrega}
          onMarcarBundle={marcarBundle}
          onSaved={() => { setModalOrden(null); setHistorial([]); setModalDetalle(null); traerOrdenes(); }}
        />
      )}

      {/* Modal confirmar entrega */}
      {ordenAentregar && (
        <ModalConfirmarEntrega
          orden={ordenAentregar}
          onConfirm={async (comisiones) => {
            const orden = ordenAentregar;
            setOrdenAentregar(null);
            // 1) Marcar como entregada (espera la confirmación del backend)
            await handleDrop({ preventDefault: () => {} }, "Entregado", orden);
            // 2) Guardar comisiones (la OC ya está entregada → pasa la validación)
            if (comisiones && comisiones.length > 0) {
              let ok = 0;
              for (const c of comisiones) {
                try {
                  const r = await axios.post(`${BASE}/comisiones_guardar.php`, new URLSearchParams({
                    orden_id: orden.id,
                    usuario_id: c.usuario_id,
                    porcentaje: c.porcentaje,
                    cargada_por_usuario_id: user?.id ?? "",
                  }));
                  if (r.data?.success) ok++;
                } catch { /* sigue con las demás */ }
              }
              if (ok > 0) toast.success(`${ok} comisión${ok !== 1 ? "es" : ""} cargada${ok !== 1 ? "s" : ""} ✓`);
            }
          }}
          onCancel={() => setOrdenAentregar(null)}
        />
      )}
    </section>
  );
};

export default SeguimientoKanban;
